import React, { useState, ChangeEvent, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Box,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { theme } from '@/theme/theme';
import {
  FileNameTypes,
  GetAssemblyAttachmentsDocument,
  GetAssemblyDetailsDocument,
  PaintBlockPointsDocument,
  useDeleteFilesMutation,
  useUpdateFileStatusMutation,
  useUploadFilesMutation,
} from '@/__generated__/graphql';
import { FileUploadStatus } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetPdfImages } from '@/hooks/useGetPdfImages';

import validateFiles from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/Tabs/ValidateFiles';
import { FileDisplay } from './FileDisplay';
import { MAX_FILE_UPLOAD_LIMIT } from '@/constants';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { FileTypeDialog } from './FileTypeDialog';
import { FileType } from '@/types';

type Props = {
  openDialog: boolean;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  referenceId: string;
  collectionName: string;
  attachments: Attachment[];
  dialogTitle: string;
  buttonText: string;
  enableUpload?: boolean;
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>;
  location: string;
};

const convertToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Result of file reading was not a string.'));
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
  });

export const UploadFileDialog = ({
  openDialog,
  setOpenDialog,
  referenceId,
  collectionName,
  attachments,
  setAttachments,
  setIsUpdating,
  buttonText,
  dialogTitle,
  enableUpload,
  location,
}: Props) => {
  const structureId = useGetStructureId();
  const [UpdateFileStatus] = useUpdateFileStatusMutation();
  const [uploadedAttachments, setUploadedAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [removeAttachments, setRemoveAttachments] = useState<string[]>([]);
  const [fileDialog, setFileDialog] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<File[] | null>(null);
  const [uploadFiles] = useUploadFilesMutation();
  const [deleteFiles] = useDeleteFilesMutation();
  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
  });
  const { getImages } = useGetPdfImages();

  useEffect(() => {
    setUploadedAttachments(attachments);
  }, [attachments]);

  const handleAddAttachment = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;
    const filesArray = Array.from(files);
    const hasImage = filesArray.some((file) => file.type.startsWith('image/'));
    if (hasImage) {
      processAttachments(filesArray, FileType.GENERAL);
    } else {
      setPendingAttachments(filesArray);
      setFileDialog(true);
    }
  };

  const processAttachments = async (files: File[], type: string) => {
    if (!files) return;
    if (uploadedAttachments.length + files.length > MAX_FILE_UPLOAD_LIMIT) {
      showSnackBar(
        `You can only upload a maximum of ${MAX_FILE_UPLOAD_LIMIT} files in total.`,
        'error'
      );
      return;
    }
    setIsUploading(true);
    const { validFiles, fileErrors } = validateFiles(files);

    if (fileErrors.length > 0) {
      showSnackBar(
        `You can only upload a maximum of ${MAX_FILE_UPLOAD_LIMIT} files in total.`,
        'error'
      );
      fileErrors.map((error) => showSnackBar(error, 'error'));
      return;
    }

    const duplicateFiles = validFiles.filter((file) =>
      uploadedAttachments.some((att) => att.name === file.name.replace(/[^\w.-]/g, '_'))
    );

    if (duplicateFiles.length > 0) {
      showSnackBar(
        `Duplicate file names found: ${duplicateFiles.map((file) => file.name).join(', ')}`,
        'error'
      );
      return;
    }

    const attachmentsPayload: Attachment[] = [];
    for (let index = 0; index < validFiles.length; index++) {
      const file = validFiles[index];

      const isImageOrVideo = file.type.startsWith('image/');
      const base64 = isImageOrVideo ? await convertToBase64(file) : '';
      if (file.type === 'application/pdf' && type === FileType.ISOMETRIC) {
        const images = await getImages(file);

        const isometric: Attachment[] = images.map((image, imageIndex) => ({
          uuid: `file-${Date.now()}-${imageIndex}`,
          name: image.name,
          type: 'image/png',
          base64: image.base64,
          isNew: true,
          size: image.size,
          file: image.file,
          uploadStatus: FileUploadStatus.Inprogress,
          fileNameType: FileNameTypes.IsometricDrawing,
        }));

        attachmentsPayload.push(...isometric);
        setIsUploading(false);
      } else {
        setIsUploading(false);
        attachmentsPayload.push({
          uuid: `file-${Date.now()}-${index}`,
          name: file.name,
          type: file.type,
          base64,
          isNew: true,
          size: file.size,
          file,
          uploadStatus: FileUploadStatus.Inprogress,
          fileNameType: FileNameTypes.UserAttachments,
        });
      }
    }
    setUploadedAttachments((previousAttachments) => [
      ...previousAttachments,
      ...attachmentsPayload,
    ]);
  };

  const handleRemoveAttachment = (index: number) => {
    const newAttachments = uploadedAttachments.filter((_, subIndex) => subIndex !== index);
    if (
      uploadedAttachments[index] &&
      !uploadedAttachments[index].isNew &&
      uploadedAttachments[index].id
    ) {
      setRemoveAttachments((previousRemovedAttachments) => [
        ...previousRemovedAttachments,
        uploadedAttachments[index].id || '',
      ]);
    }
    setUploadedAttachments(newAttachments);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setRemoveAttachments([]);
    setUploadedAttachments(attachments);
  };

  const handleUpdate = async () => {
    setIsUploading(true);
    setIsUpdating(true);
    const attachmentsToBeUploaded = uploadedAttachments.filter((file) => file.isNew);

    if (removeAttachments.length > 0) {
      setIsUpdating(true);
      setIsUploading(false);
      setOpenDialog(false);
      setAttachments(() =>
        attachments.filter(
          (attachment) => attachment.id && !removeAttachments.includes(attachment.id)
        )
      );
      await deleteFiles({
        variables: {
          input: {
            referenceId,
            collectionName,
            fileIds: removeAttachments,
          },
        },
        refetchQueries: [GetAssemblyDetailsDocument, PaintBlockPointsDocument],
        onCompleted: () => {
          setIsUpdating(false);
          showSnackBar('Attachments have been successfully updated', 'success');
        },
        onError: (error) => {
          setIsUpdating(false);
          showSnackBar(error.message, 'error');
        },
      });
    }

    const handleUploadingFiles = async (fileNameType: FileNameTypes) => {
      const attachments = attachmentsToBeUploaded.filter(
        (file) => file.fileNameType === fileNameType
      );

      if (!attachments.length) return null;

      return uploadFiles({
        variables: {
          input: {
            structureId,
            collectionName,
            referenceId,
            files: attachments.map(({ uuid, name, type, size }) => ({
              uuid: uuid || '',
              name,
              size: size || 0,
              mimeType: type,
            })),
            fileNameType,
          },
        },
        onError: (error) => {
          setIsUpdating(false);
          showSnackBar(error.message, 'error');
        },
      });
    };

    const [userResult, isoResult] = await Promise.all([
      handleUploadingFiles(FileNameTypes.UserAttachments),
      handleUploadingFiles(FileNameTypes.IsometricDrawing),
    ]);

    const allUploadedFiles = [
      ...(userResult?.data?.preSignedUrlToUploadFiles || []),
      ...(isoResult?.data?.preSignedUrlToUploadFiles || []),
    ];

    if (allUploadedFiles.length > 0) {
      const updatedAttachments = uploadedAttachments.map((attachment) => {
        const uploadedFile = allUploadedFiles.find((uploaded) => uploaded.uuid === attachment.uuid);
        if (uploadedFile) {
          return {
            ...attachment,
            url: uploadedFile.url,
            resourcePath: uploadedFile.resourcePath,
            id: uploadedFile.id,
          };
        }
        return attachment;
      });
      setAttachments(updatedAttachments);
    }

    setRemoveAttachments([]);
    setUploadedAttachments([]);
    setIsUploading(false);
    setOpenDialog(false);
  };

  const handelUpdateAttachmentStatus = async (fileId: string | undefined) => {
    if (!fileId) return;
    await UpdateFileStatus({
      variables: {
        input: {
          referenceId,
          collectionName,
          fileUpdates: [
            {
              uploadStatus: FileUploadStatus.Success,
              fileId,
            },
          ],
        },
      },
      refetchQueries: [GetAssemblyAttachmentsDocument],
      onError: (error) => {
        showSnackBar(error.message, 'error');
      },
    });
  };

  const handleConfirm = (type: string) => {
    if (pendingAttachments) {
      processAttachments(pendingAttachments, type);
    }
    setFileDialog(false);
  };

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={isUploading ? undefined : handleClose}
        disableEscapeKeyDown={isUploading}
        aria-labelledby="form-dialog-title"
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle id="form-dialog-title">{dialogTitle}</DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ minHeight: '200px' }}>
            <Typography variant="caption" sx={{ color: theme.palette.primary.dark }}>
              Upload an image, video or pdf{'  '}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              (max {MAX_FILE_UPLOAD_LIMIT} uploads)
            </Typography>
            <Grid container spacing={2} sx={{ my: 1 }}>
              <label htmlFor="file-upload" style={{ marginLeft: '15px' }}>
                <input
                  accept="image/jpeg,image/png,application/pdf,video/mp4"
                  style={{ display: 'none' }}
                  id="file-upload"
                  multiple
                  type="file"
                  onChange={handleAddAttachment}
                />
                <Box
                  sx={{
                    border: '1px dashed #B8CADD',
                    p: '16px',
                    borderRadius: 2,
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '14px',
                  }}
                >
                  <ArrowUpwardIcon style={{ color: theme.palette.text.secondary }} />
                  <Typography
                    variant="caption"
                    style={{ color: theme.palette.text.secondary, marginTop: 8 }}
                  >
                    Upload
                  </Typography>
                </Box>
              </label>
              {uploadedAttachments.map((attachment, index) => (
                <Grid item key={attachment.id || attachment.name} xs={2} md={2} sm={3}>
                  <FileDisplay
                    referenceId={referenceId}
                    collectionName={collectionName}
                    selectedAttachment={attachment}
                    selectedAttachmentIndex={index}
                    handleRemoveAttachment={handleRemoveAttachment}
                    uploadAttachmentHandler={() => handelUpdateAttachmentStatus(attachment.id)}
                    enableUpload={enableUpload}
                    setIsUpdating={setIsUpdating}
                    isUploadDialog={true}
                    showRemoveIcon
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <Stack
            direction="row"
            justifyContent="flex-start"
            spacing={2}
            sx={{ mx: '12px' }}
          ></Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              onClick={handleClose}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 2 }}
              id="media-upload-dialog-cancel-button"
            >
              Cancel
            </Button>
            <Button
              id="media-upload-dialog-update-button"
              onClick={handleUpdate}
              color="primary"
              variant="contained"
              sx={{ borderRadius: 2, background: theme.palette.primary.dark, marginRight: '12px' }}
              disabled={isUploading}
            >
              {isUploading ? <CircularProgress size={20} /> : buttonText}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <FileTypeDialog
        setFileDialog={setFileDialog}
        open={fileDialog}
        onConfirm={handleConfirm}
        location={location}
      />
    </>
  );
};

export default UploadFileDialog;
